import React, { FC, useEffect, useState, ReactNode } from 'react';
import TableSimple from 'src/components/TableSimple';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { ProductSimple } from 'src/types/product';
import { FileImportType, ProductImportErrorType } from 'src/types/productsImport';
import { CustomRenderType, TableColumnType } from 'src/types/table';
import IconButton from '@mui/material/IconButton';
import WarningIcon from '@mui/icons-material/Warning';
import CheckIcon from '@mui/icons-material/Check';
import Menu from 'src/components/Menu';
import translate from '@translate';
import Typography from 'src/components/Typography';
import { styled } from '@mui/system';
import { colors } from 'src/themes/default';

const WarningButton = styled('div')({
    '& svg path': {
        fill: colors.primary_O,
    },
});

const allColumns: TableColumnType[] = [
    {
        label: 'results',
        key: 'results',
        defaultShow: true,
        customRender: true,
    },
    {
        label: 'sku',
        key: 'PRODUCT_SKU',
        defaultShow: true,
    },
    {
        label: 'name',
        key: 'PRODUCT_NAME',
        defaultShow: true,
    },
    {
        label: 'productType',
        key: 'PRODUCT_TYPE',
        defaultShow: true,
        type: 'select',
        selectOption: 'productTypeCodes',
    },
    {
        label: 'ean',
        key: 'EAN',
        defaultShow: true,
    },
    {
        label: 'expeditionMethod',
        key: 'EXPEDITION_METHOD',
        defaultShow: true,
        type: 'select',
        selectOption: 'expeditionMethodCodes',
    },
    {
        label: 'action',
        key: 'ID',
        defaultShow: true,
        customRender: true,
    },
];

export interface ErrorRowsTableProps {
    productTypeCodes: ConstantsCodeType[];
    expeditionMethodCodes: ConstantsCodeType[];
    files: FileImportType[];
    filesError: boolean;
}

interface ValidateErrorType {
    id: number;
    errors: ProductImportErrorType[];
}

const ErrorRowsTable: FC<ErrorRowsTableProps> = (props) => {
    const [data, setData] = useState<ProductSimple[]>([]);
    const [errors, setErrors] = useState<ValidateErrorType[]>([]);

    useEffect(() => {
        const newData: ProductSimple[] = [];
        const newErrors: ValidateErrorType[] = [];
        let index: number = 1;

        props.files.forEach((file) => {
            if (file.validation?.errors?.length) {
                file.validation.errors.forEach((error) => {
                    newData.push({ ...error.product, id: index });
                    newErrors.push({ errors: error.errors, id: index });
                    index++;
                });
            }
        });
        setData(newData);
        setErrors(newErrors);
    }, [props.files]);

    const customRender: CustomRenderType = (column, row) => {
        switch (column.key) {
            case 'results':
                const errorsItem = errors.find((error) => error.id == row.id);
                if (errorsItem) {
                    return (
                        <WarningButton>
                            <Menu
                                variant="menu"
                                itemStyle={{ borderBottom: '1px solid silver' }}
                                icon={<WarningIcon color="error" />}
                                items={errorsItem.errors.map(
                                    (error: any): ReactNode => (
                                        <div>
                                            <span>{`${error.dataKey}: `}</span>
                                            <span>{translate(`error.${error.label}`)}</span>
                                        </div>
                                    )
                                )}
                            />
                        </WarningButton>
                    );
                }
                return (
                    <IconButton disabled>
                        <CheckIcon style={{ color: 'green' }} />
                    </IconButton>
                );
            case 'ID':
            default:
                return translate(row.ID ? 'productsImport.action.update' : 'productsImport.action.create');
        }
    };

    const totalCount = { create: 0, update: 0 };
    props.files
        .filter((file) => !file.imported)
        .forEach((file) => {
            totalCount.create += file.validation?.createCount || 0;
            totalCount.update += file.validation?.updateCount || 0;
        });

    return (
        <>
            <Typography gutterBottom variant="h3" sx={{ marginBottom: '20px' }}>
                {translate('productsImport.totalCountToImport', totalCount)}
            </Typography>
            {props.filesError && (
                <TableSimple
                    rowIdKey="ID"
                    rows={data}
                    columsLabelPrefix="productsImport.table"
                    allColumns={allColumns}
                    initSort={{ column: 'ID', direction: 'DESC' }}
                    selectOptions={{
                        productTypeCodes: props.productTypeCodes,
                        expeditionMethodCodes: props.expeditionMethodCodes,
                    }}
                    customRender={customRender}
                />
            )}
        </>
    );
};

export default ErrorRowsTable;
